import { Link } from 'react-router-dom';
import { ArrowRight, Car, Users, Building, Briefcase, Home, Truck } from 'lucide-react';

const WhoWeServe = () => {
  const segments = [
    {
      icon: Car,
      title: 'Individual Car Owners',
      description: 'From daily commuters to weekend drivers, we care for every car with the same dedication.',
    },
    {
      icon: Users,
      title: 'Families',
      description: 'Multiple cars? No problem. Family-friendly service with special multi-car discounts.',
    },
    {
      icon: Briefcase,
      title: 'Busy Professionals',
      description: 'Save your precious time. We wash while you work, at your office or home.',
    },
    {
      icon: Building,
      title: 'Apartment Communities',
      description: 'Group bookings for residential complexes with exclusive community offers.',
    },
    {
      icon: Home,
      title: 'Premium & Luxury Owners',
      description: 'Specialized care for high-end vehicles with premium products and techniques.',
    },
    {
      icon: Truck,
      title: 'Fleet Operators',
      description: 'Corporate fleet maintenance packages tailored to your business needs.',
    },
  ];

  const carTypes = [
    'Hatchbacks',
    'Sedans',
    'SUVs',
    'Luxury Cars',
    'Premium Vehicles',
  ];

  return (
    <div className="min-h-screen pt-20" data-testid="who-we-serve-page">
      {/* Hero */}
      <section className="py-20 bg-gradient-to-br from-blue-50 to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="inline-flex items-center px-4 py-2 bg-[#1E88E5]/10 rounded-full text-[#1E88E5] font-medium text-sm mb-6">
            Our Customers
          </div>
          <h1 className="font-heading text-4xl md:text-5xl font-bold text-[#0B2C5D] mb-6">
            Who We Serve
          </h1>
          <p className="text-lg text-[#64748B] max-w-3xl mx-auto">
            Washora proudly serves all car owners, from daily commuters to premium and luxury vehicle owners. Our services are designed to meet diverse needs while maintaining the highest quality standards.
          </p>
        </div>
      </section>

      {/* Customer Segments */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {segments.map((segment, index) => (
              <div 
                key={index}
                className="bg-white p-8 rounded-2xl border border-slate-100 shadow-sm hover:shadow-xl transition-all duration-300 group hover:-translate-y-1"
                data-testid={`segment-${index}`}
              >
                <div className="w-14 h-14 bg-[#1E88E5]/10 rounded-xl flex items-center justify-center mb-6 group-hover:bg-[#1E88E5] transition-colors">
                  <segment.icon className="h-7 w-7 text-[#1E88E5] group-hover:text-white transition-colors" />
                </div>
                <h3 className="font-heading text-xl font-bold text-[#0B2C5D] mb-3">
                  {segment.title}
                </h3>
                <p className="text-[#64748B]">{segment.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Car Types */}
      <section className="py-20 bg-[#F8FAFC]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-[#0B2C5D] mb-4">
              We Service All Vehicle Types
            </h2>
            <p className="text-[#64748B] max-w-2xl mx-auto">
              No matter what you drive, we have the expertise and products to care for it.
            </p>
          </div>
          
          <div className="flex flex-wrap justify-center gap-4">
            {carTypes.map((type, index) => (
              <div 
                key={index}
                className="bg-white px-6 py-4 rounded-full border border-slate-200 shadow-sm hover:border-[#1E88E5] hover:shadow-md transition-all"
              >
                <span className="text-[#0B2C5D] font-medium">{type}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Service Area */}
      <section className="py-20 bg-[#0B2C5D]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <h2 className="font-heading text-3xl md:text-4xl font-bold text-white mb-6">
                Our Service Area
              </h2>
              <p className="text-white/80 mb-8 leading-relaxed">
                We proudly serve Chitradurga and surrounding areas, bringing premium doorstep car wash services to Karnataka beyond the capital. As Core Karnataka's First Doorstep Car Wash Service outside Bangalore, we're committed to expanding our reach while maintaining our quality standards.
              </p>
              <div className="bg-white/10 backdrop-blur rounded-2xl p-6">
                <h3 className="font-heading text-xl font-semibold text-white mb-4">Currently Serving:</h3>
                <ul className="space-y-2 text-white/80">
                  <li className="flex items-center">
                    <div className="w-2 h-2 bg-[#F7931E] rounded-full mr-3"></div>
                    Across Chitradurga City Limits
                  </li>
                </ul>
              </div>
            </div>
            <div className="bg-gradient-to-br from-[#1E88E5] to-[#0B2C5D] rounded-3xl p-12">
              <p className="font-heading text-2xl font-bold text-white mb-4">
                "Washora — Core Karnataka's First Doorstep Car Wash, beyond the capital."
              </p>
              <p className="text-white/80">
                Expanding premium car care to every corner of Karnataka.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-heading text-3xl md:text-4xl font-bold text-[#0B2C5D] mb-6">
            Ready to Experience Premium Care?
          </h2>
          <p className="text-[#64748B] max-w-2xl mx-auto mb-8">
            No matter who you are or what you drive, we're here to serve you.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/booking"
              className="bg-[#F7931E] text-white px-8 py-4 rounded-full font-semibold hover:bg-[#E08215] transition-all transform hover:-translate-y-0.5 shadow-lg hover:shadow-orange-500/30 flex items-center justify-center"
              data-testid="serve-book-now"
            >
              Book Now <ArrowRight className="ml-2 h-5 w-5" />
            </Link>
            <Link
              to="/service-offer-plan"
              className="bg-white text-[#0B2C5D] border-2 border-[#0B2C5D] px-8 py-4 rounded-full font-semibold hover:bg-[#0B2C5D] hover:text-white transition-all flex items-center justify-center"
            >
              View Pricing
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default WhoWeServe;
